// Party invite dialog — used by the two team slots on the lobby.
// Accepts a friend code and stashes it in settingsStore.partySlots.
// When multiplayer ships, the connection layer just reads the codes and
// dispatches real invites — the UI stays the same.
import { useState } from "react";
import { useSettings } from "@/stores/settingsStore";
import { useT } from "@/i18n";
import { toast } from "sonner";
import { UserPlus, X, Send, Clock } from "lucide-react";

/**
 * @param {object} p
 * @param {boolean} p.open
 * @param {number} p.slotIndex 0 = left, 1 = right
 * @param {() => void} p.onClose
 */
export default function PartyInviteDialog({ open, slotIndex, onClose }) {
    const t = useT();
    const partySlots = useSettings((s) => s.partySlots) || [null, null];
    const update = useSettings((s) => s.update);
    const [code, setCode] = useState("");
    const existing = partySlots[slotIndex];

    if (!open) return null;

    const submit = () => {
        const c = code.trim().toUpperCase();
        if (c.length !== 6) {
            toast.error("Friend code must be 6 characters");
            return;
        }
        const next = [...partySlots];
        next[slotIndex] = { code: c, sentAt: Date.now(), status: "pending" };
        update({ partySlots: next });
        toast.success("Invite queued — will send when multiplayer ships");
        setCode("");
        onClose();
    };

    const clear = () => {
        const next = [...partySlots];
        next[slotIndex] = null;
        update({ partySlots: next });
        toast.success("Slot cleared");
        onClose();
    };

    return (
        <div
            data-testid="party-invite-dialog"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md axis-fade-in"
            onClick={onClose}
        >
            <div
                className="axis-glass axis-cut max-w-md w-[92%] p-7 border-orange-400/40"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 border border-orange-400/60 axis-cut-sm">
                        <UserPlus size={20} className="text-orange-300" />
                    </div>
                    <div>
                        <div className="axis-mono text-[10px] tracking-[0.4em] text-orange-300/80">
                            TEAM SLOT {slotIndex + 1}
                        </div>
                        <div className="axis-heading text-xl text-white tracking-widest">
                            INVITE TEAMMATE
                        </div>
                    </div>
                </div>

                <div className="axis-mono text-xs text-white/60 leading-relaxed mb-4">
                    Enter your teammate's 6-character friend code. Invites are queued
                    locally and will fire the moment online multiplayer goes live.
                </div>

                {existing && (
                    <div className="axis-cut-sm border border-white/15 bg-black/40 px-3 py-2 mb-4 flex items-center gap-3">
                        <Clock size={13} className="text-orange-300" />
                        <div className="flex-1">
                            <div className="axis-heading text-sm tracking-widest text-white">
                                {existing.code}
                            </div>
                            <div className="axis-mono text-[10px] tracking-[0.3em] text-white/50">
                                PENDING · {new Date(existing.sentAt).toLocaleDateString()}
                            </div>
                        </div>
                        <button
                            data-testid="party-clear-slot"
                            onClick={clear}
                            className="axis-btn axis-btn-ghost axis-cut-sm px-3 py-1.5 text-xs flex items-center gap-1"
                        >
                            <X size={12} /> Clear
                        </button>
                    </div>
                )}

                <input
                    data-testid="party-code-input"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
                    placeholder="ABC123"
                    maxLength={6}
                    className="w-full bg-black/60 border border-white/20 axis-cut-sm px-4 py-3 text-white axis-heading text-2xl tracking-[0.5em] text-center focus:border-orange-400 focus:outline-none"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                />

                <div className="mt-5 flex justify-end gap-3">
                    <button
                        data-testid="party-cancel"
                        onClick={onClose}
                        className="axis-btn axis-btn-ghost axis-cut-sm px-5 py-2 text-xs flex items-center gap-2"
                    >
                        <X size={13} /> {t("common.cancel")}
                    </button>
                    <button
                        data-testid="party-send"
                        onClick={submit}
                        className="axis-btn axis-cut-sm px-5 py-2 text-xs border border-orange-400 bg-orange-500/20 text-orange-200 hover:bg-orange-500/40 hover:text-white transition flex items-center gap-2"
                    >
                        <Send size={13} /> Send Invite
                    </button>
                </div>
            </div>
        </div>
    );
}
